import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { playersAPI } from '../lib/api';
import { 
  ArrowLeft, Loader2, MapPin, Calendar, Euro, User, 
  Globe, Ruler, Trophy, Video, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';

const positionColors = {
  GK: 'bg-yellow-500 text-yellow-950',
  DEF: 'bg-blue-500 text-white',
  MID: 'bg-green-500 text-white',
  FWD: 'bg-red-500 text-white',
};

const positionLabels = {
  GK: 'Portera',
  DEF: 'Defensa',
  MID: 'Mediocampista',
  FWD: 'Delantera',
};

export const PlayerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlayer();
  }, [id]);

  const loadPlayer = async () => {
    try {
      const response = await playersAPI.getOne(id);
      setPlayer(response.data);
    } catch (error) {
      console.error('Error loading player:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!player) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Jugadora no encontrada</p>
          <Button onClick={() => navigate('/players')} className="mt-4">
            Volver al listado
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const avatarUrl = player.photo_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${player.name}`;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="player-detail-page">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="relative h-48 md:h-64 bg-gradient-to-r from-indigo-600 to-violet-600">
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end gap-6">
              <img
                src={avatarUrl}
                alt={player.name}
                className="w-24 h-24 md:w-32 md:h-32 rounded-xl border-4 border-white shadow-lg object-cover bg-white"
              />
              <div className="flex-1 pb-2">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${positionColors[player.position]}`}>
                    {positionLabels[player.position]}
                  </span>
                  {player.availability === 'transfer' && (
                    <span className="badge-available">Transferencia</span>
                  )}
                  {player.availability === 'loan' && (
                    <span className="badge-loan">Préstamo</span>
                  )}
                  {player.availability === 'both' && (
                    <span className="badge-available">Disponible</span>
                  )}
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-white uppercase tracking-tight">
                  {player.name}
                </h1>
                <p className="text-white/80 mt-1">{player.club_name}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Quick info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="stat-card">
                <Calendar className="w-5 h-5 text-indigo-500 mb-2" />
                <p className="text-xs text-slate-500 uppercase tracking-wider">Edad</p>
                <p className="font-heading text-2xl font-bold text-slate-800">{player.age} años</p>
              </div>
              <div className="stat-card">
                <MapPin className="w-5 h-5 text-indigo-500 mb-2" />
                <p className="text-xs text-slate-500 uppercase tracking-wider">Nacionalidad</p>
                <p className="font-heading text-xl font-bold text-slate-800">{player.nationality}</p>
              </div>
              {player.asking_price && (
                <div className="stat-card">
                  <Euro className="w-5 h-5 text-indigo-500 mb-2" />
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Precio</p>
                  <p className="font-heading text-xl font-bold text-slate-800">
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(player.asking_price)}
                  </p>
                </div>
              )}
              {player.salary_expectation && (
                <div className="stat-card">
                  <Euro className="w-5 h-5 text-lime-500 mb-2" />
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Salario esperado</p>
                  <p className="font-heading text-xl font-bold text-slate-800">
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(player.salary_expectation)}/año
                  </p>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4 border-b border-slate-200 pb-2">
                  Información
                </h2>
                <dl className="space-y-3">
                  {player.current_club_name && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Club actual</dt>
                      <dd className="font-medium text-slate-800">{player.current_club_name}</dd>
                    </div>
                  )}
                  {player.contract_until && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Fin de contrato</dt>
                      <dd className="font-medium text-slate-800">{player.contract_until}</dd>
                    </div>
                  )}
                  {player.height && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Altura</dt>
                      <dd className="font-medium text-slate-800">{player.height} cm</dd>
                    </div>
                  )}
                  {player.preferred_foot && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Pie preferido</dt>
                      <dd className="font-medium text-slate-800 capitalize">
                        {player.preferred_foot === 'right' ? 'Diestra' : 
                         player.preferred_foot === 'left' ? 'Zurda' : 'Ambidiestra'}
                      </dd>
                    </div>
                  )}
                  {player.experience_years && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Experiencia</dt>
                      <dd className="font-medium text-slate-800">{player.experience_years} años</dd>
                    </div>
                  )}
                  {player.international_caps && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Internacionalidades</dt>
                      <dd className="font-medium text-slate-800">{player.international_caps}</dd>
                    </div>
                  )}
                  {player.languages && player.languages.length > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Idiomas</dt>
                      <dd className="font-medium text-slate-800">{player.languages.join(', ')}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div>
                <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4 border-b border-slate-200 pb-2">
                  ¿Te interesa esta jugadora?
                </h2>
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                  <p className="font-medium text-indigo-800">Contacta con Solo Cracks</p>
                  <p className="text-sm text-indigo-600 mt-1">
                    Nosotros gestionamos la operación y te ponemos en contacto con el club.
                  </p>
                </div>

                {player.video_url && (
                  <div className="mt-6">
                    <h3 className="font-heading text-sm font-bold text-slate-600 uppercase tracking-tight mb-3">
                      Video
                    </h3>
                    <a 
                      href={player.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
                    >
                      <Video className="w-4 h-4" />
                      Ver video
                    </a>
                  </div>
                )}
              </div>
            </div>

            {player.description && (
              <div className="mt-8">
                <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4 border-b border-slate-200 pb-2">
                  Descripción
                </h2>
                <p className="text-slate-600 whitespace-pre-wrap">{player.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PlayerDetailPage;
